import type { ReactElement, ReactNode } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { Transition } from 'react-transition-group';
import sizes from 'styles/config/sizes';

import { safeResizeObserver } from '@polyai/common/utils/safeResizeObserver';

import { Body } from 'components/atoms/LegacyText';
import { cn } from 'utils/cn';

export type ActionBarCornerVariant = 'rounded' | 'pill';

export interface ActionDrawerProps {
  open: boolean;
  children: ReactElement | ReactElement[];
  text?: ReactNode;
  cornerVariant?: ActionBarCornerVariant;
  secondaryAction?: ReactNode;
  isMainNavOpen?: boolean;
  width?: number;
}

const ActionDrawer = ({
  open,
  children,
  text,
  cornerVariant = 'rounded',
  secondaryAction,
  isMainNavOpen = false,
  width,
}: ActionDrawerProps) => {
  const actionBarRef = useRef<HTMLDivElement>(null);
  const [actionBarHeight, setActionBarHeight] = useState(0);

  const actionBarSizeObserver = useMemo(
    () =>
      safeResizeObserver(([actionBar]) => {
        const { height } = actionBar.target.getBoundingClientRect();
        setActionBarHeight(height);
      }),
    [],
  );

  useLayoutEffect(() => {
    if (actionBarRef.current) {
      const element = actionBarRef.current;

      actionBarSizeObserver.observe(element);

      return () => actionBarSizeObserver.unobserve(element);
    }
  }, [actionBarSizeObserver]);

  const actionDrawerRef = useRef<HTMLDivElement>(null);
  const innerTransitionRef = useRef<HTMLDivElement>(null);
  const fakeDrawerRef = useRef<HTMLDivElement>(null);
  const [actionDrawerHeight, setActionDrawerHeight] = useState(0);

  const actionDrawerSizeObserver = useMemo(
    () =>
      safeResizeObserver(([actionBar]) => {
        const { height } = actionBar.target.getBoundingClientRect();
        setActionDrawerHeight(height);
      }),
    [],
  );

  useLayoutEffect(() => {
    if (actionDrawerRef.current) {
      const element = actionDrawerRef.current;

      actionDrawerSizeObserver.observe(element);

      return () => actionDrawerSizeObserver.unobserve(element);
    }
  }, [actionDrawerSizeObserver]);

  return (
    <div>
      <Transition
        nodeRef={actionDrawerRef}
        in={open || !!secondaryAction}
        timeout={10}
      >
        {() => (
          <div
            ref={actionDrawerRef}
            className={cn(
              'pointer-events-none fixed right-0 bottom-0 z-[29]',
              'flex items-center justify-center',
              'py-sm4 px-sm3 sm:p-sm4 md:px-md2',
            )}
            style={{ left: isMainNavOpen ? `${sizes.mainNavWidth}px` : 0 }}
          >
            <Transition nodeRef={innerTransitionRef} in={open} timeout={10}>
              {/* biome-ignore lint/complexity/noExcessiveCognitiveComplexity: pre-existing */}
              {state => (
                <div
                  ref={innerTransitionRef}
                  style={
                    width
                      ? {
                          width: `${width}px`,
                          maxWidth: '100%',
                          margin: '0 auto',
                        }
                      : undefined
                  }
                >
                  <div
                    className={cn(
                      'flex flex-1 flex-wrap items-center justify-start',
                      'transition-[max-height] duration-[250ms] ease-in-out',
                    )}
                    style={{
                      maxHeight:
                        state === 'entered' ? '128px' : `${actionBarHeight}px`,
                    }}
                  >
                    {secondaryAction && (
                      <div className="pointer-events-auto my-xs3 mr-sm3">
                        {secondaryAction}
                      </div>
                    )}
                    <div
                      ref={actionBarRef}
                      className={cn(
                        'pointer-events-auto flex items-center justify-center justify-self-center gap-sm4 max-w-full text-primary-inverse bg-background-inverse transition-[transform,opacity] duration-[250ms] ease-in-out [&_button]:whitespace-nowrap',
                        cornerVariant === 'pill'
                          ? 'rounded-full'
                          : 'rounded-[4px]',
                        text ? 'px-sm1 py-xs3' : 'p-xs3',
                      )}
                      style={{
                        transform:
                          state === 'entered'
                            ? 'translateY(0)'
                            : `translateY(${actionBarHeight}px)`,
                        opacity: state === 'entered' ? 1 : 0,
                      }}
                      data-test-id="action-drawer"
                    >
                      {text && (
                        <Body
                          className={cn(
                            'font-semibold',
                            cornerVariant === 'pill' ? 'pl-xs2' : 'pl-0',
                          )}
                        >
                          {text}
                        </Body>
                      )}
                      <div className="flex gap-xs3">{children}</div>
                    </div>
                  </div>
                </div>
              )}
            </Transition>
          </div>
        )}
      </Transition>
      <Transition
        nodeRef={fakeDrawerRef}
        in={open || !!secondaryAction}
        timeout={10}
      >
        {state => (
          <div
            ref={fakeDrawerRef}
            className={cn(
              'transition-[min-height] duration-[250ms] ease-in-out',
              (state === 'entered' || state === 'exiting') && [
                'min-h-[calc(var(--drawer-height)-24px)]',
                'sm:min-h-[calc(var(--drawer-height)-32px)]',
                'md:min-h-[calc(var(--drawer-height)-48px)]',
              ],
            )}
            style={
              {
                '--drawer-height': `${actionDrawerHeight}px`,
              } as React.CSSProperties
            }
          />
        )}
      </Transition>
    </div>
  );
};

export default ActionDrawer;
